import {
  OrderPageSchema,
  OrderSchema,
  PaymentPageSchema,
  PaymentSchema,
  type ManualPaymentRequest,
  type OrderDto,
  type PlaceOrderRequest,
  type SetOrderLinesRequest,
  type SetOrderPointsRequest,
  type UpdateOrderRequest,
} from '@deli/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getJson, sendJson } from './client.js';

export interface OrderFilter {
  view?: 'awaiting_payment' | 'to_ship' | 'cod_unsettled';
  search?: string;
  status?: string;
  paymentStatus?: string;
  paymentMethod?: string;
  customerType?: string;
  customerId?: string;
  from?: string;
  to?: string;
  offset?: number;
}

export const ORDER_PAGE = 50;

export const useOrders = (filter: OrderFilter, enabled = true) =>
  useQuery({
    queryKey: ['orders', 'list', filter],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: String(ORDER_PAGE) });
      for (const [key, value] of Object.entries(filter)) {
        if (value !== undefined && value !== '' && value !== 0) params.set(key, String(value));
      }
      return getJson(`/api/v1/orders?${params}`, OrderPageSchema, { signal });
    },
    placeholderData: keepPreviousData,
    enabled,
  });

export const useOrder = (id: string) =>
  useQuery({
    queryKey: ['orders', 'card', id],
    queryFn: ({ signal }) => getJson(`/api/v1/orders/${id}`, OrderSchema, { signal }),
  });

export function useCreateOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { customerId: string }) =>
      sendJson('POST', '/api/v1/orders', body, OrderSchema),
    onSuccess: (order) => {
      client.setQueryData(['orders', 'card', order.id], order);
      void client.invalidateQueries({ queryKey: ['orders', 'list'] });
    },
  });
}

/** Every order change answers with the whole order, which replaces the cached one. */
function useOrderChange<T>(id: string, request: (input: T) => [string, string, unknown?]) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: T) => {
      const [method, path, body] = request(input);
      return sendJson(
        method as 'POST' | 'PUT' | 'PATCH',
        `/api/v1/orders/${id}${path}`,
        body ?? {},
        OrderSchema,
      );
    },
    onSuccess: (order: OrderDto) => {
      client.setQueryData(['orders', 'card', id], order);
      void client.invalidateQueries({ queryKey: ['orders', 'list'] });
      void client.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

export const useSetLines = (id: string) =>
  useOrderChange(id, (body: SetOrderLinesRequest) => ['PUT', '/lines', body]);
export const useUpdateOrder = (id: string) =>
  useOrderChange(id, (body: UpdateOrderRequest) => ['PATCH', '', body]);
export const useSetOrderPoints = (id: string) =>
  useOrderChange(id, (body: SetOrderPointsRequest) => ['PUT', '/points', body]);
export const usePlaceOrder = (id: string) =>
  useOrderChange(id, (body: PlaceOrderRequest) => ['POST', '/place', body]);
export const useCancelOrder = (id: string) =>
  useOrderChange(id, (reason: string) => ['POST', '/cancel', { reason }]);
export const useShipOrder = (id: string) =>
  useOrderChange(id, (ttn: string) => ['POST', '/ship', { ttn: ttn.trim() || null }]);
export const useDeliverOrder = (id: string) =>
  useOrderChange(id, (_: void) => ['POST', '/deliver']);
export const useReturnOrder = (id: string) =>
  useOrderChange(id, (reason: string) => ['POST', '/return', { reason }]);

export const PAYMENT_PAGE = 50;

export const usePayments = (filter: { direction?: string; offset?: number }) =>
  useQuery({
    queryKey: ['payments', filter],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: String(PAYMENT_PAGE) });
      if (filter.direction) params.set('direction', filter.direction);
      if (filter.offset) params.set('offset', String(filter.offset));
      return getJson(`/api/v1/payments?${params}`, PaymentPageSchema, { signal });
    },
    placeholderData: keepPreviousData,
  });

/** A manual payment or refund; the order it belongs to is re-fetched (its status may change). */
export function useManualPayment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ refund, ...body }: ManualPaymentRequest & { refund: boolean }) =>
      sendJson(
        'POST',
        refund ? '/api/v1/payments/manual-refund' : '/api/v1/payments/manual',
        body,
        PaymentSchema,
      ),
    onSuccess: (payment) => {
      void client.invalidateQueries({ queryKey: ['orders'] });
      void client.invalidateQueries({ queryKey: ['payments'] });
      return payment;
    },
  });
}

export const invoicePdfUrl = (orderId: string, invoiceId: string) =>
  `/api/v1/orders/${orderId}/invoices/${invoiceId}/pdf`;
